import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FiCheck, FiLock } from 'react-icons/fi';
import API from '../utils/api';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const CheckoutPage = () => {
  const { cart, subtotal, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1=address, 2=review, 3=done
  const [orderNumber, setOrderNumber] = useState('');

  const shipping = subtotal > 100 ? 0 : 9.99;
  const tax = +(subtotal * 0.1).toFixed(2);
  const total = +(subtotal + shipping + tax).toFixed(2);

  const [form, setForm] = useState({
    name: user?.name || '', phone: user?.phone || '',
    street: '', city: '', state: '', zip: '', country: 'United States',
    paymentMethod: 'card',
    notes: '',
  });

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  if (!user) return (
    <div style={{paddingTop:120,textAlign:'center'}}>
      <h2>Please login to checkout</h2>
      <Link to="/login" className="btn btn-primary" style={{marginTop:16}}>Login</Link>
    </div>
  );

  if (cart.length === 0 && step < 3) return (
    <div style={{paddingTop:120,textAlign:'center'}}>
      <h2>Your cart is empty</h2>
      <Link to="/products" className="btn btn-primary" style={{marginTop:16}}>Shop Now</Link>
    </div>
  );

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const { data } = await API.post('/orders', {
        items: cart.map(i => ({ productId: i.id, name: i.name, price: i.price, quantity: i.quantity, size: i.size, color: i.color })),
        shippingAddress: { name: form.name, phone: form.phone, street: form.street, city: form.city, state: form.state, zip: form.zip, country: form.country },
        paymentMethod: form.paymentMethod,
        notes: form.notes,
      });
      setOrderNumber(data.order.orderNumber);
      clearCart();
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Order failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (step === 3) return (
    <div style={{paddingTop:120,paddingBottom:60,textAlign:'center'}}>
      <div style={{maxWidth:520,margin:'0 auto',padding:'0 24px'}}>
        <div style={{width:72,height:72,background:'#059669',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 24px'}}>
          <FiCheck size={32} color="#fff"/>
        </div>
        <h1 style={{fontSize:'2rem',marginBottom:12}}>Order Placed! 🎉</h1>
        <p style={{color:'var(--text-muted)',marginBottom:8}}>Thank you for your order.</p>
        <p style={{fontWeight:700,fontSize:'1.1rem',color:'var(--primary)',marginBottom:24}}>Order #: {orderNumber}</p>
        <p style={{color:'var(--text-muted)',marginBottom:32,fontSize:'0.95rem'}}>You'll receive a confirmation email shortly. Your order will be processed and shipped within the estimated timeframe.</p>
        <div style={{display:'flex',gap:12,justifyContent:'center',flexWrap:'wrap'}}>
          <Link to="/dashboard" className="btn btn-primary">View My Orders</Link>
          <Link to="/products" className="btn btn-outline">Continue Shopping</Link>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{paddingTop:100,paddingBottom:60}}>
      <div className="container">
        <h1 style={{marginBottom:32}}>Checkout</h1>
        <div style={{display:'grid',gridTemplateColumns:'1fr 340px',gap:32,alignItems:'start'}}>
          <div>
            {/* Steps */}
            <div style={{display:'flex',gap:8,marginBottom:32,alignItems:'center'}}>
              {[1,2].map(s => (
                <React.Fragment key={s}>
                  <div style={{display:'flex',alignItems:'center',gap:8}}>
                    <div style={{width:28,height:28,borderRadius:'50%',background:step>=s?'var(--primary)':'var(--border)',color:step>=s?'#fff':'var(--text-muted)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'0.8rem',fontWeight:700}}>{s}</div>
                    <span style={{fontSize:'0.9rem',fontWeight:500,color:step>=s?'var(--text)':'var(--text-muted)'}}>{s===1?'Shipping Address':'Review Order'}</span>
                  </div>
                  {s < 2 && <div style={{flex:1,height:1,background:'var(--border)'}}/>}
                </React.Fragment>
              ))}
            </div>

            {step === 1 && (
              <div style={{background:'#fff',border:'1px solid var(--border)',borderRadius:'var(--radius-lg)',padding:32}}>
                <h3 style={{marginBottom:24}}>Shipping Address</h3>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
                  {[['name','Full Name','text'],['phone','Phone Number','tel'],['street','Street Address','text'],['city','City','text'],['state','State / Province','text'],['zip','ZIP / Postal Code','text']].map(([name,label,type])=>(
                    <div key={name} className="form-group" style={name==='street'?{gridColumn:'1/-1'}:{}}>
                      <label>{label}</label>
                      <input className="form-control" type={type} name={name} value={form[name]} onChange={handleChange} placeholder={label} required/>
                    </div>
                  ))}
                  <div className="form-group">
                    <label>Country</label>
                    <select className="form-control" name="country" value={form.country} onChange={handleChange}>
                      {['United States','Canada','United Kingdom','Australia','India'].map(c=><option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Payment Method</label>
                    <select className="form-control" name="paymentMethod" value={form.paymentMethod} onChange={handleChange}>
                      <option value="card">Credit / Debit Card</option>
                      <option value="paypal">PayPal</option>
                      <option value="bank_transfer">Bank Transfer</option>
                    </select>
                  </div>
                  <div className="form-group" style={{gridColumn:'1/-1'}}>
                    <label>Order Notes (optional)</label>
                    <textarea className="form-control" name="notes" value={form.notes} onChange={handleChange} rows={3} placeholder="Any special instructions..."/>
                  </div>
                </div>
                <button className="btn btn-primary btn-lg" style={{marginTop:8}}
                  disabled={!form.name||!form.street||!form.city||!form.zip}
                  onClick={() => setStep(2)}>Continue to Review →</button>
              </div>
            )}

            {step === 2 && (
              <div style={{background:'#fff',border:'1px solid var(--border)',borderRadius:'var(--radius-lg)',padding:32}}>
                <h3 style={{marginBottom:20}}>Review Your Order</h3>
                {cart.map(item => (
                  <div key={item.cartKey} style={{display:'flex',gap:14,padding:'12px 0',borderBottom:'1px solid var(--border)',alignItems:'center'}}>
                    <img src={item.image} alt={item.name} style={{width:56,height:56,objectFit:'cover',borderRadius:8}}/>
                    <div style={{flex:1}}>
                      <div style={{fontWeight:600,fontSize:'0.9rem'}}>{item.name}</div>
                      <div style={{fontSize:'0.78rem',color:'var(--text-muted)'}}>Qty: {item.quantity} {item.size&&`· ${item.size}`} {item.color&&`· ${item.color}`}</div>
                    </div>
                    <div style={{fontWeight:700}}>${(item.price*item.quantity).toFixed(2)}</div>
                  </div>
                ))}
                <div style={{marginTop:20,padding:16,background:'var(--bg-soft)',borderRadius:'var(--radius)'}}>
                  <strong style={{fontSize:'0.85rem'}}>Shipping to:</strong>
                  <p style={{fontSize:'0.85rem',color:'var(--text-muted)',marginTop:4}}>{form.name} · {form.street}, {form.city}, {form.state} {form.zip}, {form.country}</p>
                </div>
                <div style={{display:'flex',gap:12,marginTop:24}}>
                  <button className="btn btn-outline" onClick={()=>setStep(1)}>← Back</button>
                  <button className="btn btn-primary btn-lg" style={{flex:1,justifyContent:'center'}} onClick={handleSubmit} disabled={loading}>
                    <FiLock size={16}/>{loading ? 'Placing Order...' : `Place Order · $${total.toFixed(2)}`}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Summary */}
          <div style={{background:'var(--bg-soft)',borderRadius:'var(--radius-lg)',padding:24,border:'1px solid var(--border)',position:'sticky',top:90}}>
            <h3 style={{marginBottom:16,fontSize:'1rem'}}>Order Summary</h3>
            {[['Subtotal',`₹${subtotal.toFixed(2)}`],['Shipping',shipping===0?'FREE':`₹${shipping.toFixed(2)}`],['Tax',`₹${tax.toFixed(2)}`]].map(([k,v])=>(
              <div key={k} style={{display:'flex',justifyContent:'space-between',marginBottom:10,fontSize:'0.88rem'}}>
                <span style={{color:'var(--text-muted)'}}>{k}</span><span>{v}</span>
              </div>
            ))}
            <div style={{borderTop:'1px solid var(--border)',paddingTop:14,display:'flex',justifyContent:'space-between',fontWeight:800,fontFamily:'var(--font-display)'}}>
              <span>Total</span><span style={{color:'var(--primary)'}}>₹{total.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
